import { useEffect, useMemo, useRef, useState, type CSSProperties, type DragEvent, type FormEvent, type ReactNode } from 'react';
import {
  applyMove,
  buildLanes,
  COLUMN_LABEL,
  COLUMNS,
  columnOf,
  createBoardTask,
  dueTone,
  getBoard,
  laneToColumnIndex,
  MISC_LANE,
  moveTask,
  PRIORITY_WORD,
  type BoardColumns,
  type BoardItem,
  type Bucket,
  type ColumnKey,
  type Lane,
  type LaneMode,
  type LaneProject,
} from '../lib/board.js';
import { dueChip, ymdInHomeZone } from '../lifeos/format.js';
import Sheet from '../lifeos/Sheet.js';
import { sphereColor, useSpheres } from '../lifeos/spheres.js';
import TaskEditSheet from '../lifeos/TaskEditSheet.js';
import { errorText, useToast } from '../lifeos/toast.js';
import { Loader } from '../lifeos/ui.js';
import { useLoad } from '../lifeos/useLoad.js';
import Icon from '../ui/Icon.js';

/** Columns a compact board folds into chips ("Later 0 · Done 3") until opened. */
const FOLDED: readonly ColumnKey[] = ['later', 'done'];

/** "Ferguson's › SEO brief" → "SEO brief". */
export function leafName(path: string): string {
  const parts = path.split('›');
  return (parts[parts.length - 1] ?? path).trim() || path;
}

/** Long-press on touch opens the Move menu after this many ms. */
const LONG_PRESS_MS = 450;

/** Collapsed swimlanes, remembered per browser (storage may be unavailable). */
const COLLAPSED_KEY = 'vitals.board.collapsedLanes';
function readCollapsed(): Set<string> {
  try {
    const raw = localStorage.getItem(COLLAPSED_KEY);
    const v: unknown = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []);
  } catch {
    return new Set();
  }
}
function writeCollapsed(s: ReadonlySet<string>): void {
  try {
    localStorage.setItem(COLLAPSED_KEY, JSON.stringify([...s]));
  } catch {
    // storage blocked: collapse still works for this visit
  }
}

/** One rendered column cell: the whole board's column, or one lane's slice of it. */
interface Cell {
  /** Drag-over identity: the column key, or `<lane>|<column>`. */
  over: string;
  key: ColumnKey;
  rows: BoardItem[];
  label: string;
  /** Lane-local drop index → index in the whole column. */
  toColumn: (i: number) => number;
}

/**
 * The focus board (spec 2026-10-14-workspaces): This week · Next · Later ·
 * Waiting · Done. Drag a card between or within columns on desktop; on a
 * phone long-press (right-click on desktop) opens "Move to…" (columns, Edit, Delete). Hovering a card shows Done and Delete.
 * Moves are optimistic: the card jumps at once and jumps back with a toast if
 * the server says no. Tapping a card opens the task edit sheet.
 *
 * With `lanes`, rows are split into horizontal swimlanes (one per top-level
 * project + Misc, or one per job). A drop into another lane's column changes
 * only the bucket/status — never the project; that is done in the edit sheet.
 */
export default function Board({
  sphere = null,
  project = null,
  projectSphere,
  compact = false,
  hideProjectId,
  lanes: laneMode = null,
  projects = [],
  onChanged,
}: {
  sphere?: string | null;
  /** Filter to a project (and its sub-projects). */
  project?: string | null;
  /** The sphere quick-added items land in when a project is filtered. */
  projectSphere?: string;
  /** Inside a project page: narrower columns, one quiet add per column. */
  compact?: boolean;
  /** Don't print this project's chip on its own cards (sub-projects still show). */
  hideProjectId?: string;
  /** Swimlanes: per project (one job selected) or per job (All). Ignored when compact. */
  lanes?: LaneMode | null;
  /** Projects, for lane names, parents and priority. */
  projects?: readonly LaneProject[];
  onChanged?: () => void;
}): JSX.Element {
  const board = useLoad(() => getBoard({ sphere, project }), [sphere, project]);
  const spheres = useSpheres();
  const toast = useToast();
  const [cols, setCols] = useState<BoardColumns | null>(null);
  const colsRef = useRef<BoardColumns | null>(null);
  const [menu, setMenu] = useState<BoardItem | null>(null);
  const [editing, setEditing] = useState<BoardItem | null>(null);
  const [drag, setDrag] = useState<{ id: string; over: string | null; index: number } | null>(null);
  const today = ymdInHomeZone(new Date());
  // Compact (project page): Later and Done fold into chips until opened.
  const [unfolded, setUnfolded] = useState<ReadonlySet<ColumnKey>>(new Set());
  const toggleFold = (k: ColumnKey): void =>
    setUnfolded((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(readCollapsed);
  const toggleLane = (key: string): void =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      writeCollapsed(next);
      return next;
    });
  const [showEmpty, setShowEmpty] = useState(false);
  const mode: LaneMode | null = compact ? null : laneMode;

  useEffect(() => {
    if (board.data) {
      colsRef.current = board.data;
      setCols(board.data);
    }
  }, [board.data]);

  const lanes = useMemo<Lane[] | null>(
    () => (mode && cols ? buildLanes(cols, mode, projects, spheres, { sphere, showEmpty }) : null),
    [mode, cols, projects, spheres, sphere, showEmpty],
  );

  const set = (next: BoardColumns): void => {
    colsRef.current = next;
    setCols(next);
  };

  async function move(id: string, to: ColumnKey, index: number): Promise<void> {
    const before = colsRef.current;
    if (!before) return;
    const r = applyMove(before, id, to, index);
    if (!r) return;
    set(r.cols);
    try {
      // Pin unranked cards above the drop point first, so the new rank sorts where it was dropped.
      await Promise.all(r.fixes.map((f) => moveTask(f.id, { boardRank: f.boardRank })));
      await moveTask(id, r.body);
      onChanged?.();
    } catch (err) {
      // Only roll back if nothing else moved since.
      if (colsRef.current === r.cols) set(before);
      toast.error(`Couldn't move “${r.item.title}”: ${errorText(err)}`);
    }
  }

  /** Delete = drop (soft): the card leaves at once; the request waits out the Undo window. */
  function remove(id: string): void {
    const before = colsRef.current;
    if (!before) return;
    const k = COLUMNS.find((c) => before[c.key].some((x) => x.id === id))?.key;
    if (!k) return;
    const item = before[k].find((x) => x.id === id)!;
    set({ ...before, [k]: before[k].filter((x) => x.id !== id) });
    toast.defer({
      text: `Deleted “${item.title}”`,
      commit: () => moveTask(id, { status: 'dropped' }),
      onUndo: () => {
        const cur = colsRef.current ?? before;
        if (cur[k].some((x) => x.id === id)) return;
        const at = Math.min(before[k].findIndex((x) => x.id === id), cur[k].length);
        set({ ...cur, [k]: [...cur[k].slice(0, at), item, ...cur[k].slice(at)] });
      },
      onError: () => void board.reload(),
      onDone: () => onChanged?.(),
      failText: `Couldn't delete “${item.title}”`,
    });
  }

  async function add(bucket: Bucket, title: string): Promise<void> {
    try {
      const sphereKey = projectSphere ?? sphere ?? undefined;
      await createBoardTask({ title, bucket, status: 'next', ...(sphereKey ? { sphereKey } : {}), ...(project ? { projectId: project } : {}) });
      await board.reload();
      onChanged?.();
    } catch (err) {
      toast.error(`Couldn't add it: ${errorText(err)}`);
      throw err;
    }
  }

  /** Index in the cell the pointer is over: before the first card whose middle is below it. */
  function dropIndex(e: DragEvent<HTMLElement>, rows: number): number {
    const cards = [...e.currentTarget.querySelectorAll<HTMLElement>('[data-card]')];
    const i = cards.findIndex((c) => {
      const r = c.getBoundingClientRect();
      return e.clientY < r.top + r.height / 2;
    });
    return i < 0 ? rows : i;
  }

  /** The lane a row sits in (lane mode). */
  const laneFor = (id: string): Lane | undefined => lanes?.find((l) => COLUMNS.some((c) => l.cols[c.key].some((x) => x.id === id)));

  /** Where the Move sheet thinks a row is: lane-local in lane mode. */
  const where = (item: BoardItem): { key: ColumnKey; index: number; rows: (k: ColumnKey) => readonly BoardItem[] } => {
    const lane = laneFor(item.id);
    const c = colsRef.current;
    const rowsOf = (k: ColumnKey): readonly BoardItem[] => (lane ? lane.cols[k] : c?.[k] ?? []);
    const key = columnOf(item);
    const inKey = rowsOf(key).findIndex((x) => x.id === item.id);
    if (inKey >= 0) return { key, index: inKey, rows: rowsOf };
    for (const k of COLUMNS) {
      const i = rowsOf(k.key).findIndex((x) => x.id === item.id);
      if (i >= 0) return { key: k.key, index: i, rows: rowsOf };
    }
    return { key, index: 0, rows: rowsOf };
  };

  /** Card chip: sub-project inside its project lane; the project path on the job lanes; else the old rule. */
  const chipFor = (t: BoardItem, lane: Lane | null): string | null => {
    const path = t.projectPath ?? t.projectName;
    if (!t.projectId || !path) return null;
    if (!lane) return t.projectId !== hideProjectId ? leafName(path) : null;
    if (mode === 'sphere') return path;
    if (lane.projectId && t.projectId === lane.projectId) return null;
    return leafName(path);
  };

  function renderCell(cell: Cell, lane: Lane | null, head: ReactNode): JSX.Element {
    const over = drag?.over === cell.over;
    const meta = COLUMNS.find((c) => c.key === cell.key)!;
    return (
      <section
        key={cell.over}
        role="listitem"
        className={`bd__col bd__col--${cell.key}${lane ? ' bd__cell' : ''}${lane && cell.rows.length === 0 ? ' bd__cell--empty' : ''}${over ? ' bd__col--over' : ''}`}
        aria-label={cell.label}
        onDragOver={(e) => {
          if (!drag) return;
          e.preventDefault();
          e.dataTransfer.dropEffect = 'move';
          const index = dropIndex(e, cell.rows.length);
          if (drag.over !== cell.over || drag.index !== index) setDrag({ ...drag, over: cell.over, index });
        }}
        onDrop={(e) => {
          e.preventDefault();
          const id = drag?.id ?? e.dataTransfer.getData('text/plain');
          const index = dropIndex(e, cell.rows.length);
          setDrag(null);
          if (id) void move(id, cell.key, cell.toColumn(index));
        }}
      >
        {head}
        <ul className="bd__list">
          {cell.rows.map((t, i) => (
            <Card
              key={t.id}
              item={t}
              today={today}
              color={sphereColor(spheres, t.sphereKey)}
              chip={chipFor(t, lane)}
              dropBefore={over && drag?.index === i && drag.id !== t.id}
              onOpen={() => setEditing(t)}
              onMenu={() => setMenu(t)}
              onDone={t.status === 'done' ? undefined : () => void move(t.id, 'done', 0)}
              onDelete={() => remove(t.id)}
              onDragStart={(e) => {
                e.dataTransfer.setData('text/plain', t.id);
                e.dataTransfer.effectAllowed = 'move';
                setDrag({ id: t.id, over: null, index: -1 });
              }}
              onDragEnd={() => setDrag(null)}
              dragging={drag?.id === t.id}
            />
          ))}
          {over && drag && drag.index >= cell.rows.length && <li className="bd__drop" aria-hidden="true" />}
        </ul>
        {cell.rows.length === 0 && !over && !lane && <p className="bd__empty">{meta.empty}</p>}
      </section>
    );
  }

  const [adding, setAdding] = useState<ColumnKey | null>(null);
  const columnHead = (c: (typeof COLUMNS)[number], count: number): JSX.Element => (
    <header className="bd__head">
      <h3 className="bd__title">{c.label}</h3>
      <span className="bd__count" aria-label={`${count} items`}>
        {count}
      </span>
      {c.quickAdd && (
        <button
          type="button"
          className={`bd__plus${adding === c.key ? ' bd__plus--on' : ''}`}
          aria-label={`Add to ${c.label}`}
          aria-expanded={adding === c.key}
          onClick={() => setAdding((a) => (a === c.key ? null : c.key))}
        >
          <Icon name="plus" size={16} />
        </button>
      )}
      {compact && FOLDED.includes(c.key) && (
        <button type="button" className="bd__fold" onClick={() => toggleFold(c.key)} aria-label={`Hide ${c.label}`}>
          Hide
        </button>
      )}
    </header>
  );

  function renderLanes(all: BoardColumns, ls: Lane[]): JSX.Element {
    return (
      <>
        <div className="bd__lanehead" role="group" aria-label="Columns">
          <div className="bd__lanegap" aria-hidden="true" />
          {COLUMNS.map((c) => (
            <div key={c.key} className={`bd__headcell bd__col--${c.key}`}>
              {columnHead(c, all[c.key].length)}
              {c.quickAdd && adding === c.key && (
                <QuickAdd label={c.label} compact={false} onAdd={(t) => add(c.key as Bucket, t)} onClose={() => setAdding(null)} />
              )}
            </div>
          ))}
        </div>
        {ls.length === 0 && <p className="bd__nolanes">Nothing on the board yet. Add something to This week above.</p>}
        {ls.map((l) => {
          const shut = collapsed.has(l.key);
          return (
            <section
              key={l.key}
              className={`bd__lane${shut ? ' bd__lane--shut' : ''}${l.key === MISC_LANE ? ' bd__lane--misc' : ''}`}
              aria-label={l.name}
              style={l.color ? ({ '--job': l.color } as CSSProperties) : undefined}
            >
              <button type="button" className="bd__lanebar" aria-expanded={!shut} onClick={() => toggleLane(l.key)}>
                <Icon name="chevron-right" size={16} className="bd__lanechev" />
                {l.color && <span className="bd__lanedot" aria-hidden="true" />}
                <span className="bd__lanename">{l.name}</span>
                <span className="bd__lanecount" aria-label={`${l.count} items`}>
                  {l.count}
                </span>
              </button>
              {!shut && (
                <div className="bd__cols" role="list" aria-label={`${l.name} board`}>
                  {COLUMNS.map((c) =>
                    renderCell(
                      {
                        over: `${l.key}|${c.key}`,
                        key: c.key,
                        rows: l.cols[c.key],
                        label: `${l.name}: ${c.label}`,
                        toColumn: (i) =>
                          laneToColumnIndex(
                            colsRef.current?.[c.key] ?? [],
                            l.cols[c.key].map((x) => x.id),
                            i,
                          ),
                      },
                      l,
                      <p className="bd__celltitle">
                        {c.label} <span className="bd__count">{l.cols[c.key].length}</span>
                      </p>,
                    ),
                  )}
                </div>
              )}
            </section>
          );
        })}
        <button type="button" className="bd__showempty" aria-pressed={showEmpty} onClick={() => setShowEmpty((v) => !v)}>
          {showEmpty ? 'Hide empty lanes' : 'Show empty lanes'}
        </button>
      </>
    );
  }

  return (
    <div className={`bd${compact ? ' bd--compact' : ''}${lanes ? ' bd--lanes' : ''}`}>
      <Loader state={board}>
        {() =>
          !cols ? null : lanes ? (
            renderLanes(cols, lanes)
          ) : (
            <>
              <div className="bd__cols" role="list" aria-label="Focus board">
                {COLUMNS.filter((c) => !compact || !FOLDED.includes(c.key) || unfolded.has(c.key)).map((c) =>
                  renderCell(
                    { over: c.key, key: c.key, rows: cols[c.key], label: c.label, toColumn: (i) => i },
                    null,
                    <>
                      {columnHead(c, cols[c.key].length)}
                      {c.quickAdd && adding === c.key && (
                        <QuickAdd label={c.label} compact={compact} onAdd={(t) => add(c.key as Bucket, t)} onClose={() => setAdding(null)} />
                      )}
                    </>,
                  ),
                )}
              </div>
              {compact && FOLDED.some((k) => !unfolded.has(k)) && (
                <div className="bd__folds" role="group" aria-label="Folded columns">
                  {COLUMNS.filter((c) => FOLDED.includes(c.key) && !unfolded.has(c.key)).map((c) => (
                    <button
                      key={c.key}
                      type="button"
                      className={`bd__foldchip${drag?.over === c.key ? ' bd__foldchip--over' : ''}`}
                      aria-expanded={false}
                      onClick={() => toggleFold(c.key)}
                      onDragOver={(e) => {
                        if (!drag) return;
                        e.preventDefault();
                        e.dataTransfer.dropEffect = 'move';
                        if (drag.over !== c.key) setDrag({ ...drag, over: c.key, index: 0 });
                      }}
                      onDragLeave={() => drag?.over === c.key && setDrag({ ...drag, over: null })}
                      onDrop={(e) => {
                        e.preventDefault();
                        const id = drag?.id ?? e.dataTransfer.getData('text/plain');
                        setDrag(null);
                        if (id) void move(id, c.key, 0);
                      }}
                    >
                      {c.label} <span className="bd__count">{cols[c.key].length}</span>
                    </button>
                  ))}
                </div>
              )}
            </>
          )
        }
      </Loader>

      {menu &&
        (() => {
          const at = where(menu);
          return (
            <MoveSheet
              item={menu}
              at={at}
              onMove={(to, index) => {
                setMenu(null);
                const all = colsRef.current?.[to] ?? [];
                const ids = at.rows(to).map((x) => x.id);
                void move(menu.id, to, lanes ? laneToColumnIndex(all, ids, index) : index);
              }}
              onEdit={() => {
                setEditing(menu);
                setMenu(null);
              }}
              onDelete={() => {
                setMenu(null);
                remove(menu.id);
              }}
              onClose={() => setMenu(null)}
            />
          );
        })()}
      {editing && (
        <TaskEditSheet
          task={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            void board.reload();
            onChanged?.();
          }}
        />
      )}
    </div>
  );
}


function QuickAdd({
  label,
  compact,
  onAdd,
  onClose,
}: {
  label: string;
  compact: boolean;
  onAdd: (title: string) => Promise<void>;
  onClose: () => void;
}): JSX.Element {
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    const t = value.trim();
    if (!t || busy) return;
    setBusy(true);
    try {
      await onAdd(t);
      setValue('');
    } catch {
      // the board already showed the error; keep the text so nothing is lost
    } finally {
      setBusy(false);
    }
  }
  return (
    <form className="bd__add" onSubmit={(e) => void submit(e)}>
      <Icon name="plus" size={16} className="bd__addicon" />
      <input
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={compact ? 'Add' : `Add to ${label}`}
        aria-label={`Add to ${label}`}
        disabled={busy}
        autoFocus
        onKeyDown={(e) => e.key === 'Escape' && onClose()}
        onBlur={() => !value.trim() && !busy && onClose()}
      />
    </form>
  );
}

function Card({
  item: t,
  today,
  color,
  chip,
  dropBefore,
  dragging,
  onOpen,
  onMenu,
  onDone,
  onDelete,
  onDragStart,
  onDragEnd,
}: {
  item: BoardItem;
  today: string;
  color: string;
  /** The project chip's text (null = none); the full path shows in the tooltip. */
  chip: string | null;
  dropBefore: boolean;
  dragging: boolean;
  onOpen: () => void;
  /** Touch long-press: the Move sheet (no drag on phones). */
  onMenu: () => void;
  onDone?: () => void;
  onDelete: () => void;
  onDragStart: (e: DragEvent<HTMLLIElement>) => void;
  onDragEnd: () => void;
}): JSX.Element {
  const press = useRef<{ timer: ReturnType<typeof setTimeout>; x: number; y: number } | null>(null);
  const fired = useRef(false);
  const cancel = (): void => {
    if (press.current) clearTimeout(press.current.timer);
    press.current = null;
  };
  const due = dueChip(t.dueOn, today);
  const tone = dueTone(t.dueOn, today);
  const done = t.status === 'done';
  const project = t.projectPath ?? t.projectName;
  return (
    <>
      {dropBefore && <li className="bd__drop" aria-hidden="true" />}
      <li
        data-card={t.id}
        className={`bd__card${dragging ? ' bd__card--dragging' : ''}${done ? ' bd__card--done' : ''}`}
        draggable
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onPointerDown={(e) => {
          fired.current = false;
          if (e.pointerType === 'mouse') return;
          const { clientX: x, clientY: y } = e;
          press.current = {
            x,
            y,
            timer: setTimeout(() => {
              fired.current = true;
              press.current = null;
              onMenu();
            }, LONG_PRESS_MS),
          };
        }}
        onPointerMove={(e) => {
          const p = press.current;
          if (p && Math.hypot(e.clientX - p.x, e.clientY - p.y) > 10) cancel();
        }}
        onPointerUp={cancel}
        onPointerCancel={cancel}
        onContextMenu={(e) => {
          // Right-click (desktop) opens the same Move / Edit / Delete sheet as a long-press.
          e.preventDefault();
          if (!fired.current) onMenu();
        }}
      >
        <button
          type="button"
          className="bd__cardmain"
          onClick={() => {
            if (fired.current) {
              fired.current = false;
              return;
            }
            onOpen();
          }}
        >
          <span className="bd__cardtitle">{t.title}</span>
          <span className="bd__cardmeta">
            {t.priority === 1 && (
              <span className="bd__high" title={PRIORITY_WORD[1]}>
                High
              </span>
            )}
            {chip && (
              <span className="bd__proj" style={{ '--job': color } as CSSProperties} title={project ?? chip}>
                <span className="bd__projdot" aria-hidden="true" />
                <span className="bd__projname">{chip}</span>
              </span>
            )}
            {due && !done && <span className={`bd__due bd__due--${tone}`}>{due.label}</span>}
            {t.status === 'waiting' && t.waitingOn && <span className="bd__waiton">on {t.waitingOn}</span>}
            {t.contactName && (
              <span className="bd__who" title={t.contactName} aria-label={`With ${t.contactName}`}>
                {t.contactName.trim()[0]?.toUpperCase() ?? '?'}
              </span>
            )}
          </span>
        </button>
        <span className="bd__acts">
          {onDone && (
            <button type="button" className="bd__act bd__act--done" aria-label={`Mark ${t.title} done`} title="Done" onClick={onDone}>
              <Icon name="check" size={16} />
            </button>
          )}
          <button type="button" className="bd__act bd__act--del" aria-label={`Delete ${t.title}`} title="Delete" onClick={onDelete}>
            <Icon name="trash" size={16} />
          </button>
        </span>
      </li>
    </>
  );
}

/** Touch long-press: every column as a big button, plus Edit and Delete. Order within a column is drag-only. */
function MoveSheet({
  item,
  at,
  onMove,
  onEdit,
  onDelete,
  onClose,
}: {
  item: BoardItem;
  at: { key: ColumnKey; index: number };
  onMove: (to: ColumnKey, index: number) => void;
  onEdit: () => void;
  onDelete: () => void;
  onClose: () => void;
}): JSX.Element {
  return (
    <Sheet title={item.title} onClose={onClose}>
      <p className="bd__sheetlabel">Move to</p>
      <div className="bd__moveto" role="group" aria-label="Move to">
        {COLUMNS.map((c) => (
          <button
            key={c.key}
            type="button"
            className={`bd__movebtn${c.key === at.key ? ' bd__movebtn--here' : ''}`}
            aria-current={c.key === at.key ? 'true' : undefined}
            disabled={c.key === at.key}
            onClick={() => onMove(c.key, 0)}
          >
            {COLUMN_LABEL[c.key]}
          </button>
        ))}
      </div>
      <div className="lo-sheet__list">
        <button type="button" className="lo-sheet__row" onClick={onEdit}>
          Edit details
        </button>
        <button type="button" className="lo-sheet__row bd__sheetdel" onClick={onDelete}>
          Delete
        </button>
      </div>
    </Sheet>
  );
}
